import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { ChannelReadingPoint, ChannelReadingsResponse } from '../api/contracts';
import type { DateRangePickerProps } from '@cloudscape-design/components';
import { pickBucketMs, resampleToBuckets } from '../lib/energyChartSeries';

/**
 * GET /api/readings?channelId=&from=&to=
 *
 * `enabled: channelId != null` -- this hook is only meaningful once a
 * channel has been selected from the topology tree.
 */
export function useChannelReadings(channelId: string | null, fromIso: string, toIso: string) {
  return useQuery({
    queryKey: ['channel-readings', channelId, fromIso, toIso],
    queryFn: () =>
      apiClient.get<ChannelReadingsResponse>(
        `/api/readings?channelId=${encodeURIComponent(channelId as string)}` +
          `&from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
      ),
    enabled: channelId != null,
  });
}

export interface ChartPoint {
  x: Date;
  y: number;
}

/**
 * Splits a channel's reading points into contiguous line-chart segments,
 * breaking wherever `watts` is `null` (sampleCount === 0 -- no data).
 *
 * WHY segments instead of one series with nulls dropped: this Cloudscape
 * version's LineChart (mixed-line-bar-chart/line-series.js) builds each
 * series with a plain d3 `line()` generator and no `.defined()` accessor,
 * so a single series simply connecting the points on either side of a gap
 * would draw a straight interpolated line THROUGH the gap -- which is
 * exactly the "outage misread as a real zero/dip" failure this rule exists
 * to prevent (the read-side mirror of the Emporia/EcoFlow write-side
 * sample-count rule). Returning one array per contiguous run of non-null
 * points, rendered as independent series in the page, guarantees no path
 * is ever drawn across a null -- there is no line-drawing code path that
 * could paper over a gap, because no single series ever spans one.
 *
 * A `null` point is dropped, never coerced to 0 -- it contributes no (x, y)
 * pair to any segment.
 */
export function toChartSegments(points: readonly ChannelReadingPoint[]): ChartPoint[][] {
  const segments: ChartPoint[][] = [];
  let current: ChartPoint[] = [];

  for (const p of points) {
    if (p.watts === null) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
      continue;
    }
    current.push({ x: new Date(p.ts), y: p.watts });
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/**
 * Same contract as `toChartSegments` (a `null`/missing point breaks the
 * line, never bridges it), but averages readings into `bucketMs` buckets
 * first via `resampleToBuckets`, matching the Overview page's chart-
 * readability fix -- see docs/PLAN.md. Bucket width is picked from the
 * selected range's span, so a 24-hour window resamples finer than a 7-day
 * one.
 */
export function toResampledChartSegments(points: readonly ChannelReadingPoint[], bucketMs: number): ChartPoint[][] {
  const resampled = resampleToBuckets(
    points,
    (p) => p.ts,
    (p) => p.watts ?? undefined,
    bucketMs,
  );

  const segments: ChartPoint[][] = [];
  let current: ChartPoint[] = [];
  let prevBucketStart: number | null = null;

  for (const { ts, value } of resampled) {
    const bucketStart = new Date(ts).getTime();
    if (prevBucketStart !== null && bucketStart - prevBucketStart > bucketMs) {
      if (current.length > 0) segments.push(current);
      current = [];
    }
    current.push({ x: new Date(ts), y: value });
    prevBucketStart = bucketStart;
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/** Picks a bucket width for a channel-readings chart spanning `fromIso`..`toIso`. */
export function pickReadingsBucketMs(fromIso: string, toIso: string): number {
  const spanMs = new Date(toIso).getTime() - new Date(fromIso).getTime();
  return pickBucketMs(Math.max(spanMs, 60_000));
}

// ---------------------------------------------------------------------------
// Default range
// ---------------------------------------------------------------------------

/** Default chart window: last 24 hours, ending now. */
export function defaultReadingsRange(now: Date = new Date()): { from: string; to: string } {
  const to = now.toISOString();
  const from = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  return { from, to };
}

const RELATIVE_UNIT_MS: Record<DateRangePickerProps.TimeUnit, number> = {
  second: 1_000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
  week: 7 * 86_400_000,
  month: 30 * 86_400_000,
  year: 365 * 86_400_000,
};

/**
 * Resolves a `DateRangePicker` value into concrete ISO bounds.
 *
 * `now` MUST be supplied by the caller as a value computed ONCE (e.g.
 * `useMemo(() => new Date(), [])`), never a fresh `new Date()` evaluated
 * inside this function. This function previously fell back to
 * `defaultReadingsRange()` with no `now` argument whenever `value` was null
 * -- and that function defaults `now` to a fresh `new Date()` internally.
 * Called on every render (which happens constantly -- any state update,
 * parent re-render, etc.), that produced a NEW from/to pair every time,
 * which changes `useChannelReadings`' TanStack Query key every render,
 * which meant every fetch got abandoned by a newer one before it could ever
 * resolve -- the Circuits page's Channel Detail panel spun forever
 * regardless of which channel was selected, confirmed live in production.
 *
 * This also previously never handled the DateRangePicker's 'relative' value
 * type at all (only 'absolute' was handled) -- so picking a preset like
 * "Last 48 hours" silently fell through to the same always-regenerating
 * default as picking nothing.
 */
export function toRangeBounds(value: DateRangePickerProps.Value | null, now: Date): { from: string; to: string } {
  if (value && value.type === 'absolute' && value.startDate && value.endDate) {
    return { from: value.startDate, to: value.endDate };
  }
  if (value && value.type === 'relative') {
    const ms = value.amount * RELATIVE_UNIT_MS[value.unit];
    return { from: new Date(now.getTime() - ms).toISOString(), to: now.toISOString() };
  }
  return defaultReadingsRange(now);
}
